/**
 * Hook usePermissions
 * 
 * Hook React para verificar permissões do usuário no frontend
 * 
 * Funcionalidades:
 * - Verificar se usuário tem permissão para módulo/ação
 * - Carregar permissões do usuário
 * - Verificar se usuário é admin
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import {
  RECEIVABLE_GRANULAR_ACTIONS,
  FINANCIAL_REPORT_GRANULAR_ACTIONS,
  SALES_GRANULAR_ACTIONS,
  resolveFinancialReceivablePermission,
  resolveFinancialReportPermission,
  resolveSalesPermission,
  normalizeSalesPermissions
} from '../utils/permissions'

/**
 * Constantes de módulos e ações (deve estar sincronizado com backend)
 */
export const MODULES = {
  ERP: 'erp',
  DASHBOARD: 'dashboard',
  COCKPIT: 'cockpit',
  PRODUCTS: 'products',
  STOCK: 'stock',
  SALES: 'sales',
  QUOTES: 'quotes',
  PEOPLE: 'people',
  FINANCIAL: 'financial',
  SETTINGS: 'settings',
  DIGITAL_CERTIFICATE: 'digital_certificate',
  DOCUMENTS: 'documents',
  ATTENDANCE: 'attendance',
  PROSPECTING: 'prospecting',
  PURCHASES: 'purchases',
  MESSAGES: 'messages',
  REPORTS: 'reports',
  PATIENTS: 'patients',
  DUTY_ROSTERS: 'duty_rosters',
  MY_SHIFTS: 'my_shifts'
}

export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  ERP_OPERATIONAL_ASSISTANT: 'operational_assistant',
  PRODUCTS_GROUPS: 'groups',
  PRODUCTS_BRANDS: 'brands',
  PRODUCTS_UNITS: 'units',
  PRODUCTS_IMAGES: 'images',
  PRODUCTS_EDIT_DESCRIPTION: 'edit_description',
  PRODUCTS_EDIT_BRAND: 'edit_brand',
  PRODUCTS_EDIT_GROUP: 'edit_group',
  PRODUCTS_EDIT_CUBAGE: 'edit_cubage',
  PRODUCTS_HIDE_COST: 'hide_cost',
  PRODUCTS_GRIDS: 'grids',
  PRODUCT_REQUEST_VIEW: 'product_request_view',
  PRODUCT_REQUEST_CREATE: 'product_request_create',
  PRODUCT_REQUEST_APPROVE: 'product_request_approve',
  PRODUCT_REQUEST_REJECT: 'product_request_reject',
  STOCK_MOVEMENTS: 'movements',
  STOCK_CREATE_MOVEMENT: 'create_movement',
  STOCK_INVOICES: 'invoices',
  STOCK_IMPORT_INVOICE: 'import_invoice',
  STOCK_RETURNS_VIEW: 'returns_view',
  STOCK_RETURNS_CREATE: 'returns_create',
  STOCK_RETURNS_UPDATE: 'returns_update',
  STOCK_RETURNS_APPROVE: 'returns_approve',
  STOCK_CREDITS_VIEW: 'credits_view',
  STOCK_CREDITS_CREATE: 'credits_create',
  STOCK_CREDITS_APPROVE: 'credits_approve',
  STOCK_CREDITS_USE: 'credits_use',
  STOCK_AUDIT_VIEW: 'audit_view',
  STOCK_AUDIT_CREATE: 'audit_create',
  STOCK_AUDIT_EXECUTE: 'audit_execute',
  STOCK_AUDIT_APPLY: 'audit_apply',
  STOCK_AUDIT_CANCEL: 'audit_cancel',
  STOCK_AUDIT_REOPEN: 'audit_reopen',
  STOCK_AUDITS_EDIT_RESPONSIBLE: 'audits_edit_responsible',
  STOCK_ADJUSTMENT_VIEW: 'adjustment_view',
  STOCK_ADJUSTMENT_CREATE: 'adjustment_create',
  STOCK_ADJUSTMENT_APPROVE: 'adjustment_approve',
  STOCK_ADJUSTMENT_REJECT: 'adjustment_reject',
  SALES_PDV_VIEW: 'pdv_view',
  SALES_RELATION_VIEW: 'relation_view',
  SALES_PRESALE: 'presale',
  SALES_PRESALE_LIST_VIEW: 'presale_list_view',
  SALES_ONLINE_VIEW: 'online_view',
  SALES_ONLINE_UPDATE: 'online_update',
  SALES_ONLINE_DELETE: 'online_delete',
  SALES_NFE_VIEW: 'nfe_view',
  SALES_NFE_CREATE: 'nfe_create',
  SALES_NFE_UPDATE: 'nfe_update',
  SALES_SPECIAL_TABLES_VIEW: 'special_tables_view',
  SALES_SPECIAL_TABLES_MANAGE: 'special_tables_manage',
  SALES_FINALIZE: 'finalize',
  SALES_CANCEL: 'cancel',
  SALES_CASH_REGISTER_RECONCILIATION: 'cash_register_reconciliation',
  SALES_SELLER_ASSISTANT: 'seller_assistant',
  SALES_TV_DISPLAY: 'tv_display',
  SALES_RETURN_NFE: 'return_nfe',
  SALES_DATA_READ: 'data_read',
  PEOPLE_CUSTOMERS: 'customers',
  PEOPLE_CUSTOMERS_BONUS_MANUAL: 'customers_bonus_manual',
  PEOPLE_SUPPLIERS: 'suppliers',
  PEOPLE_USERS: 'users',
  PEOPLE_ACCESS_GROUPS: 'access_groups',
  FINANCIAL_ACCOUNTS: 'accounts',
  FINANCIAL_CATEGORIES: 'categories',
  FINANCIAL_COST_CENTERS: 'cost_centers',
  FINANCIAL_PAYMENT_METHODS: 'payment_methods',
  FINANCIAL_RECEIPT_METHODS: 'receipt_methods',
  FINANCIAL_PAYABLES: 'payables',
  FINANCIAL_RECEIVABLES: 'receivables',
  FINANCIAL_RECEIVABLES_VIEW: 'receivables_view',
  FINANCIAL_RECEIVABLES_CREATE: 'receivables_create',
  FINANCIAL_RECEIVABLES_UPDATE: 'receivables_update',
  FINANCIAL_RECEIVABLES_DELETE: 'receivables_delete',
  FINANCIAL_RECEIVABLES_MARK_PAID: 'receivables_mark_paid',
  FINANCIAL_RECEIVABLES_BOLETO: 'receivables_boleto',
  FINANCIAL_CASH_FLOW: 'cash_flow',
  FINANCIAL_CARD_RECONCILIATION: 'card_reconciliation',
  FINANCIAL_REPORTS: 'financial_reports',
  FINANCIAL_DRE: 'dre_view',
  FINANCIAL_DFC: 'dfc_view',
  FINANCIAL_BALANCE_SHEET: 'balance_sheet_view',
  FINANCIAL_PURCHASE_APPROVAL: 'purchase_approval',
  FINANCIAL_BOLETOS_VIEW: 'boletos_view',
  FINANCIAL_BOLETOS_DASHBOARD: 'boletos_dashboard',
  SETTINGS_CERTIFICATES: 'certificates',
  DIGITAL_CERTIFICATE_MANAGE: 'manage',
  DOCUMENTS_DASHBOARD: 'dashboard',
  DOCUMENTS_COMPANY_VIEW: 'company_view',
  DOCUMENTS_COMPANY_EDIT: 'company_edit',
  DOCUMENTS_COMPANY_UPLOAD: 'company_upload',
  DOCUMENTS_USER_VIEW: 'user_view',
  DOCUMENTS_CATEGORIES_VIEW: 'categories_view',
  // Ações específicas de Orçamentos
  QUOTES_APPROVE: 'approve',
  QUOTES_CANCEL: 'cancel',
  QUOTES_OPEN: 'open',
  QUOTES_RESERVE: 'reserve',
  QUOTES_CONVERT: 'convert',
  QUOTES_REVERT_TO_DRAFT: 'revert_to_draft',
  // Ações específicas de Ponto Eletrônico
  ATTENDANCE_VIEW: 'view',
  ATTENDANCE_CREATE: 'create',
  ATTENDANCE_REPORTS: 'reports',
  ATTENDANCE_ACCOUNTING: 'accounting',
  ATTENDANCE_LOG: 'log_view',
  ATTENDANCE_LOG_EDIT: 'log_edit',
  // Ações específicas de Prospecção
  PROSPECTING_VIEW: 'view',
  PROSPECTING_CREATE: 'create',
  PROSPECTING_UPDATE: 'update',
  PROSPECTING_LEADS: 'leads',
  PROSPECTING_PROPOSALS: 'proposals',
  PROSPECTING_REDISTRIBUTE: 'redistribute',
  PROSPECTING_WALLET: 'wallet',
  // Ações específicas de Compras
  PURCHASES_DASHBOARD: 'dashboard',
  PURCHASES_FOLLOW_UP: 'follow_up',
  PURCHASES_INTELLIGENCE: 'intelligence',
  // Ações específicas de Mensagens
  MESSAGES_SEND: 'send',
  // Ações específicas de Relatórios (hub /erp/relatorios)
  REPORTS_RECEIVABLES: 'receivables',
  REPORTS_STOCK: 'stock',
  REPORTS_SALES: 'sales',
  PATIENTS_DELETE_PATIENT: 'delete_patient',
  PATIENTS_ARCHIVE: 'archive',
  PATIENTS_VITALS: 'vitals',
  PATIENTS_VITALS_WEIGHT: 'vitals_weight',
  PATIENTS_VITALS_HEIGHT: 'vitals_height',
  PATIENTS_VITALS_RECORD: 'vitals_record',
  PATIENTS_VITALS_EDIT: 'vitals_edit',
  PATIENTS_VITALS_SHORTCUT: 'vitals_shortcut',
  PATIENTS_MEDICATIONS: 'medications',
  PATIENTS_MEDICATIONS_PRESCRIBE: 'medications_prescribe',
  PATIENTS_MEDICATIONS_ADMINISTER: 'medications_administer',
  PATIENTS_CONTACTS: 'contacts',
  PATIENTS_CONTACTS_EDIT: 'contacts_edit',
  PATIENTS_FINANCIAL: 'financial',
  PATIENTS_EVOLUTIONS: 'evolutions',
  PATIENTS_EVOLUTIONS_PRINT: 'evolutions_print',
  PATIENTS_EVOLUTION_ROLES: 'evolution_roles',
  PATIENTS_DOCUMENTS: 'documents',
  PATIENTS_THERAPY: 'therapy',
  PATIENTS_THERAPY_ACTIVITIES: 'therapy_activities',
  PATIENTS_THERAPY_GOALS: 'therapy_goals',
  PATIENTS_NUTRITION: 'nutrition',
  PATIENTS_NUTRITION_PRESCRIPTIONS: 'nutrition_prescriptions',
  PATIENTS_NUTRITION_PRESCRIPTIONS_CREATE: 'nutrition_prescriptions_create',
  PATIENTS_NUTRITION_RECORD: 'nutrition_record',
  PATIENTS_NUTRITION_WATER: 'nutrition_water',
  PATIENTS_CHART_PRINT: 'chart_print',
  PATIENTS_MANAGE_ACCESS: 'manage_access',
  PATIENTS_BYPASS_ACCESS: 'bypass_access',
  PATIENTS_SHIFT_REQUIRED: 'shift_required',
  PATIENTS_DASHBOARD: 'dashboard',
  PATIENTS_NOTES: 'notes',
  PATIENTS_NOTES_RECORD: 'notes_record',
  DUTY_ROSTERS_GENERATE: 'generate',
  DUTY_ROSTERS_EDIT: 'edit',
  DUTY_ROSTERS_SAVE: 'save',
  PATIENTS_EDIT_CHART: 'edit_chart'
}

// Cache de permissões no localStorage
const PERMISSIONS_CACHE_KEY = 'wfmed_permissions_cache_v2'
const PERMISSIONS_CACHE_TIMESTAMP_KEY = 'wfmed_permissions_cache_timestamp_v2'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

/**
 * Salvar permissões no cache
 */
const savePermissionsToCache = (userId, permissions) => {
  try {
    const cacheData = {
      userId,
      permissions,
      timestamp: Date.now()
    }
    localStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(cacheData))
    localStorage.setItem(PERMISSIONS_CACHE_TIMESTAMP_KEY, Date.now().toString())
  } catch (error) {
    console.warn('[usePermissions] Erro ao salvar cache:', error)
  }
}

/**
 * Carregar permissões do cache
 */
const loadPermissionsFromCache = (userId) => {
  try {
    const cached = localStorage.getItem(PERMISSIONS_CACHE_KEY)
    const timestamp = localStorage.getItem(PERMISSIONS_CACHE_TIMESTAMP_KEY)
    
    if (!cached || !timestamp) return null
    
    const cacheData = JSON.parse(cached)
    const cacheAge = Date.now() - parseInt(timestamp)
    
    // Verificar se o cache é do mesmo usuário e ainda é válido
    if (cacheData.userId === userId && cacheAge < CACHE_DURATION) {
      return normalizeSalesPermissions(cacheData.permissions)
    }
    
    // Cache expirado ou de outro usuário
    return null
  } catch (error) {
    console.warn('[usePermissions] Erro ao carregar cache:', error)
    return null
  }
}

/**
 * Limpar cache de permissões
 */
const clearPermissionsCache = () => {
  try {
    localStorage.removeItem(PERMISSIONS_CACHE_KEY)
    localStorage.removeItem(PERMISSIONS_CACHE_TIMESTAMP_KEY)
  } catch (error) {
    console.warn('[usePermissions] Erro ao limpar cache:', error)
  }
}

/**
 * Hook para verificar permissões do usuário
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuth()
  const [permissions, setPermissions] = useState({})
  const [loading, setLoading] = useState(true)
  const userId = user?.id ?? null
  const userRole = user?.role ?? null
  const accessGroupId = user?.access_group_id ?? null

  const loadPermissions = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)

      if (accessGroupId) {
        const response = await api.get(`/api/people/access-groups/${accessGroupId}?t=${Date.now()}`)
        if (response.data.success && response.data.data) {
          const group = response.data.data
          let perms = {}

          try {
            if (typeof group.permissions === 'string') {
              perms = JSON.parse(group.permissions)
            } else {
              perms = group.permissions || {}
            }
          } catch (error) {
            console.error('[usePermissions] Erro ao parsear permissões:', error)
            perms = {}
          }

          perms = normalizeSalesPermissions(perms)
          setPermissions(perms)
          savePermissionsToCache(userId, perms)
        } else {
          console.warn('[usePermissions] Resposta sem dados válidos')
          setPermissions({})
        }
      } else {
        console.warn('[usePermissions] Usuário sem access_group_id')
        setPermissions({})
      }
    } catch (error) {
      if (error.response?.status === 403) {
        console.error('[usePermissions] Acesso negado ao carregar permissões:', error.response.data?.message)
      } else {
        console.error('[usePermissions] Erro ao carregar permissões:', error)
      }
      setPermissions({})
    } finally {
      if (!silent) setLoading(false)
    }
  }, [userId, accessGroupId])

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setPermissions({})
      setLoading(false)
      clearPermissionsCache()
      return
    }

    if (userRole === 'admin') {
      const allPermissions = {}
      Object.values(MODULES).forEach(module => {
        allPermissions[module] = {}
        Object.values(ACTIONS).forEach(action => {
          allPermissions[module][action] = true
        })
      })
      setPermissions(allPermissions)
      savePermissionsToCache(userId, allPermissions)
      setLoading(false)
      return
    }

    const cachedPermissions = loadPermissionsFromCache(userId)
    if (cachedPermissions) {
      setPermissions(cachedPermissions)
      setLoading(false)
      loadPermissions(true)
      return
    }

    loadPermissions(false)
  }, [userId, userRole, accessGroupId, isAuthenticated, loadPermissions])

  /**
   * Verifica se usuário tem permissão para módulo e ação
   */
  const hasPermission = useCallback((module, action) => {
    if (!isAuthenticated || !user) {
      console.log(`[hasPermission] Usuário não autenticado ou não encontrado`, { module, action })
      return false
    }

    // Admin sempre tem permissão
    if (user.role === 'admin') {
      console.log(`[hasPermission] Usuário é admin - permitindo acesso`, { module, action })
      return true
    }

    // Verificar permissão específica
    if (!permissions[module]) {
      console.log(`[hasPermission] Módulo ${module} não encontrado nas permissões`, { 
        module, 
        action, 
        availableModules: Object.keys(permissions),
        permissions 
      })
      return false
    }

    let hasAccess = permissions[module][action] === true
    if (module === MODULES.FINANCIAL && RECEIVABLE_GRANULAR_ACTIONS.includes(action)) {
      hasAccess = resolveFinancialReceivablePermission(permissions, action)
    }
    if (module === MODULES.FINANCIAL && FINANCIAL_REPORT_GRANULAR_ACTIONS.includes(action)) {
      hasAccess = resolveFinancialReportPermission(permissions, action)
    }
    if (module === MODULES.SALES && (SALES_GRANULAR_ACTIONS.includes(action) || [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE].includes(action))) {
      hasAccess = resolveSalesPermission(permissions, action)
    }
    console.log(`[hasPermission] Verificando permissão`, {
      module,
      action,
      hasAccess,
      modulePermissions: permissions[module],
      specificValue: permissions[module][action]
    })

    return hasAccess
  }, [user, permissions, isAuthenticated])

  /**
   * Verifica se usuário tem acesso ao módulo (qualquer ação)
   */
  const hasModuleAccess = useCallback((module) => {
    if (!isAuthenticated || !user) {
      return false
    }

    // Admin sempre tem acesso
    if (user.role === 'admin') {
      return true
    }

    // Verificar se módulo existe e tem pelo menos uma ação habilitada
    if (!permissions[module]) {
      return false
    }

    const modulePermissions = permissions[module]
    return Object.values(modulePermissions).some(enabled => enabled === true)
  }, [user, permissions, isAuthenticated])

  /**
   * Verifica se usuário tem qualquer uma das permissões listadas
   */
  const hasAnyPermission = useCallback((permissionList) => {
    return permissionList.some(({ module, action }) => hasPermission(module, action))
  }, [hasPermission])

  /**
   * Verifica se usuário tem todas as permissões listadas
   */
  const hasAllPermissions = useCallback((permissionList) => {
    return permissionList.every(({ module, action }) => hasPermission(module, action))
  }, [hasPermission])

  return {
    permissions,
    loading,
    hasPermission,
    hasModuleAccess,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin: user?.role === 'admin',
    reloadPermissions: loadPermissions,
    refreshPermissions: () => {
      console.log('[usePermissions] Forçando atualização de permissões')
      clearPermissionsCache()
      loadPermissions()
    },
    clearCache: clearPermissionsCache
  }
}

// Exportar função para limpar cache globalmente
export { clearPermissionsCache }

export default usePermissions

